/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useContext, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useMessage } from "./useMessage";
import { usePasswordValidation } from "./usePasswordValidation";
import { LoginUserContext } from "../providers/LoginUserProvider";
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute
} from "amazon-cognito-identity-js"
import awsConfiguration from '../awsConfiguration'
import awsCognitoMessage from "../awsCognitoMessage";

const userPool = new CognitoUserPool({
  UserPoolId: awsConfiguration.UserPoolId,
  ClientId: awsConfiguration.ClientId,
})
/*
curl -X POST  \
  -H "Content-Type: application/json"  \
  -d '{"userName":"soulful.man.jp@gmail.com", "password":"S3m6i1y4"}'  \
  https://evb7iabjra.execute-api.ap-northeast-1.amazonaws.com/v1/auth
*/
export const useConfirm = () => {
  const navigate = useNavigate();
  const { showMessage } = useMessage();
  const authCtx = useContext(LoginUserContext);
  const { resetPasswordExpiredState } = usePasswordValidation();

  const [loading, setLoading] = useState(false);
  const AUTH_MANAGER_URL: string =
    "https://194q1sinm0.execute-api.us-east-1.amazonaws.com/v1";
  type IPostResponse = {
    access?: string;
    token?: string;
    newPasswordRequired?: string;
  };
  const login = useCallback(
    (id: string, currentPassword: string, newPassword: string) => {
      setLoading(true);
      var user = {
        userName: id,
        password: currentPassword,
        newPassword: newPassword
      };

      axios
        .post<IPostResponse>(AUTH_MANAGER_URL + "/auth", user)
        .then((res) => {
          if (res.data) {
            if (res.data.newPasswordRequired) {
              setLoading(false);
              navigate("/confirm");
            } else {
              if (res.data.token) {
                authCtx.login(res.data.token,"","");
              }
              setLoading(false);
              showMessage({ title: "ログインしました", status: "success" });
              navigate("/home");
            }
          } else {
            showMessage({ title: "ユーザーが見つかりません", status: "error" });
            setLoading(false);
          }
        })
        .catch((err) => {
          // エラーメッセージを日本語化
          const msg = err.message || '';
          const translatedMsg = awsCognitoMessage[msg] || msg;
          showMessage({ title: translatedMsg, status: "error" });
          setLoading(false);
        });
    },
    [navigate, showMessage]
  );
  const signIn = useCallback(
    (id: string, currentPassword: string, newPassword: string) => {
      setLoading(true);
      const authenticationDetails = new AuthenticationDetails({
        Username : id,
        Password : currentPassword
      })
      const cognitoUser = new CognitoUser({
        Username: id,
        Pool: userPool
      })
      cognitoUser
        .authenticateUser(authenticationDetails, {
          onSuccess: (result) => {
            // get the ID token
            var idToken = result.getIdToken().getJwtToken();
            var AccessToken = result.getAccessToken().getJwtToken();
            authCtx.login(idToken,"","");
            setLoading(false);
            showMessage({ title: "ログインしました。", status: "success" });
            navigate("/home");
          },
          onFailure:(err) => {
            console.log(err);
            // エラーメッセージを日本語化
            const msg = err.message || '';
            const translatedMsg = awsCognitoMessage[msg] || msg;
            showMessage({ title: translatedMsg, status: "error" });
            setLoading(false);  
        },
        newPasswordRequired:(userAttributes, requiredAttributes) => {
          // These attributes are not mutable and should be removed from map.
          delete userAttributes.email_verified;
          delete userAttributes['custom:tenant_id'];
          console.log(requiredAttributes);
          delete userAttributes.email;
          
          // パスワード変更日を追加
          userAttributes['custom:password_changed_at'] = new Date().toISOString();
          
          cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
            onSuccess: (result) => {
              setLoading(false);
              showMessage({ title: "登録しました。", status: "success" });
              navigate("/login");
            },
            onFailure:(err) => {
              console.log(err);
              // エラーメッセージを日本語化
              const msg = err.message || '';
              const translatedMsg = awsCognitoMessage[msg] || msg;
              showMessage({ title: translatedMsg, status: "error" });
              setLoading(false);
            }
          });
        }
      });  
    },
    [navigate, showMessage]
  );
  const confirmPassword = useCallback(
    (id: string, currentPassword: string, newPassword: string) => {
      setLoading(true);
      
      // 現在の認証済みユーザーを取得
      const currentUser = userPool.getCurrentUser();
      
      if (!currentUser) {
        setLoading(false);
        showMessage({ title: "ログインが必要です。", status: "error" });
        navigate("/login");
        return;
      }

      // セッションを取得して認証状態を確認
      currentUser.getSession((err: any, session: any) => {
        if (err || !session.isValid()) {
          setLoading(false);
          showMessage({ title: "セッションが無効です。再ログインしてください。", status: "error" });
          navigate("/login");
          return;
        }

        // 認証済みユーザーでパスワード変更を実行
        currentUser.changePassword(currentPassword, newPassword, (changeErr, result) => {
          if (changeErr) {
            console.log(changeErr);
            let msg: string = changeErr['message'];
            if (msg.includes("Attempt limit exceeded")) {
              msg = "パスワード変更の試行回数制限を超えました。しばらくしてから再試行してください。";
            } else if (msg.includes("Password has previously been used")) {
              msg = "過去に使用したパスワードは再利用できません。別のパスワードを設定してください。";
            } else if (msg.includes("Incorrect username or password")) {
              msg = "パスワードが違います。";
            }
            showMessage({ title: msg, status: "error" });
            setLoading(false);
          } else {
            // パスワード変更成功時にカスタム属性を更新
            const attributes = [
              new CognitoUserAttribute({
                Name: 'custom:password_changed_at',
                Value: new Date().toISOString()
              })
            ];

            currentUser.updateAttributes(attributes, (updateErr, updateResult) => {
              if (updateErr) {
                console.error('Failed to update password changed date:', updateErr);
              } else {
                console.log('Password changed date updated successfully');
              }
              
              // パスワード変更完了後はログアウトして再ログインを促す
              setLoading(false);
              
              // パスワード有効期限切れ状態をリセット
              resetPasswordExpiredState();
              
              // ログアウト処理
              authCtx.logout();
              
              // ログイン画面に遷移（メッセージ表示は Login.tsx で行う）
              navigate("/login", { 
                state: { 
                  passwordChanged: true
                } 
              });
            });
          }
        });
      });
    },
    [navigate, showMessage, authCtx, resetPasswordExpiredState]
  );

  const forgotPassword = useCallback(
    (id: string) => {
      setLoading(true);
      const cognitoUser = new CognitoUser({
        Username: id,
        Pool: userPool
      })
      cognitoUser
        .forgotPassword({
        onSuccess: (result) => {
          setLoading(false);
          showMessage({ title: "確認コードを送信しました。", status: "success" });
          navigate("/confirm/" + id,{state:{forgotPW : true}});
        },
        onFailure:(err) => {
          console.log(err);
          // エラーメッセージを日本語化
          const msg = err.message || '';
          const translatedMsg = awsCognitoMessage[msg] || msg;
          showMessage({ title: translatedMsg, status: "error" });
          setLoading(false);
        }});  
    },
    [navigate, showMessage]
  );

  // パスワードリセット時の確認コード確認
  const confirmForgotPassword = useCallback(
    (id: string, confirmationCode: string, newPassword: string) => {
      setLoading(true);
      const cognitoUser = new CognitoUser({
        Username: id,
        Pool: userPool
      })
      
      cognitoUser.confirmPassword(confirmationCode, newPassword, {
        onSuccess: (result) => {
          // パスワードリセット成功後、新しいパスワードでログインして日時を更新
          const authenticationDetails = new AuthenticationDetails({
            Username: id,
            Password: newPassword
          });

          cognitoUser.authenticateUser(authenticationDetails, {
            onSuccess: (authResult) => {
              // 認証成功後にパスワード変更日時を更新
              const attributes = [
                new CognitoUserAttribute({
                  Name: 'custom:password_changed_at',
                  Value: new Date().toISOString()
                })
              ];

              cognitoUser.updateAttributes(attributes, (updateErr, updateResult) => {
                if (updateErr) {
                  console.error('Failed to update password changed date after reset:', updateErr);
                } else {
                  console.log('Password changed date updated successfully after reset');
                }
                
                // 属性更新後はログアウトしてログイン画面へ
                cognitoUser.signOut();
                
                setLoading(false);
                showMessage({ 
                  title: "パスワードをリセットしました", 
                  status: "success",
                  description: "新しいパスワードでログインしてください"
                });
                navigate("/login");
              });
            },
            onFailure: (authErr) => {
              console.error('Authentication failed after password reset:', authErr);
              // 認証に失敗してもパスワードリセット自体は成功
              setLoading(false);
              showMessage({ 
                title: "パスワードをリセットしました", 
                status: "success",
                description: "新しいパスワードでログインしてください"
              });
              navigate("/login");
            }
          });
        },
        onFailure: (err) => {
          console.log(err);
          // エラーメッセージを日本語化
          const msg = err.message || '';
          const translatedMsg = awsCognitoMessage[msg] || msg;
          showMessage({ title: translatedMsg, status: "error" });
          setLoading(false);
        }
      });
    },
    [navigate, showMessage]
  );

  return { login, loading, signIn, confirmPassword, forgotPassword, confirmForgotPassword };
};
